import { Card } from 'Card';
import { useEventLogger, useTargetBrandConfig } from 'FrontRoyalAngular';
import { memo, useCallback } from 'react';
import { type SidebarBoxKey } from 'AdmissionsGuidance';
import { useIsPrimaryMobileBox } from '../hooks/useIsPrimaryMobileBox';
import { ImportantCtaBoxHeader } from './ImportantCtaBoxHeader';
import { ImportantCtaBoxIcon } from './ImportantCtaBoxIcon';
import { ImportantCtaBoxDescription } from './ImportantCtaBoxDescription';
import { ImportantCtaBoxCta } from './ImportantCtaBoxCta';
import { useMobileBoxContext } from '../MobileSidebar/MobileBoxContext';

type Props = {
    headerSubtitle: string;
    headerTitle: string;
    icon?: React.ReactNode | 'default';
    iconCircle?: boolean;
    color?: 'primary' | 'purple' | 'yellow';
    ctaText?: string;
    onPressCta?: () => void;
    ctaDisabled?: boolean;
    children: React.ReactNode; // This is the description text
    testId?: string;
    boxKey: SidebarBoxKey;
    radius?: 2 | 5 | 8;
    cardWrapperBorder?: 'default' | 'none' | 'allButStart';
};

function ImportantCtaBoxComponent({
    color = 'primary',
    headerSubtitle,
    headerTitle,
    icon = 'default',
    iconCircle,
    ctaText,
    onPressCta,
    ctaDisabled,
    children,
    testId,
    boxKey,
    radius = 5,
    cardWrapperBorder = 'default',
}: Props) {
    const { importantCtaBoxDefaultIcon, brandNameShort } = useTargetBrandConfig();
    const EventLogger = useEventLogger();
    const _icon =
        icon === 'default' && importantCtaBoxDefaultIcon ? (
            <img src={importantCtaBoxDefaultIcon} alt={`${brandNameShort} Logo`} />
        ) : (
            icon
        );
    const mobileBoxConfig = useMobileBoxContext();
    const isPrimaryMobileBox = useIsPrimaryMobileBox(boxKey);
    const handleCtaPress = useCallback(() => {
        EventLogger.log('student_dashboard_sidebar_box:clicked_button', {
            type: `${boxKey}:cta_button`,
        });
        onPressCta?.();
    }, [onPressCta, EventLogger, boxKey]);

    return (
        <ImportantCtaBoxPresentation
            color={color}
            headerSubtitle={headerSubtitle}
            headerTitle={headerTitle}
            icon={_icon}
            iconCircle={icon === 'default' ? false : iconCircle}
            ctaText={ctaText}
            ctaDisabled={ctaDisabled}
            handleCtaPress={handleCtaPress}
            testId={testId}
            boxKey={boxKey}
            radius={radius}
            cardWrapperBorder={isPrimaryMobileBox ? 'allButStart' : cardWrapperBorder}
            isInMobileDrawer={!!mobileBoxConfig}
        >
            {children}
        </ImportantCtaBoxPresentation>
    );
}

export function ImportantCtaBoxPresentation({
    color = 'primary',
    headerSubtitle,
    headerTitle,
    icon = 'default',
    iconCircle,
    ctaText,
    ctaDisabled,
    handleCtaPress,
    children,
    testId,
    radius = 5,
    cardWrapperBorder = 'default',
    isInMobileDrawer,
}: Omit<Props, 'onPressCta'> & { handleCtaPress: () => void; isInMobileDrawer: boolean }) {
    return (
        <Card
            width="full"
            shadow={false}
            padding="top-6"
            radius={isInMobileDrawer ? 8 : radius}
            startBorderColor={color}
            wrapperBorder={cardWrapperBorder}
        >
            <div data-testid={`important-cta-box-${testId}`}>
                <ImportantCtaBoxHeader subtitle={headerSubtitle} title={headerTitle} />
                <ImportantCtaBoxIcon icon={icon} circle={iconCircle} color={color} />
                <ImportantCtaBoxDescription hasCta={!!ctaText}>{children}</ImportantCtaBoxDescription>
                {ctaText ? (
                    <ImportantCtaBoxCta
                        color={color === 'yellow' ? 'primary' : color}
                        onPressCta={handleCtaPress}
                        ctaText={ctaText}
                        ctaDisabled={ctaDisabled}
                    />
                ) : null}
            </div>
        </Card>
    );
}

export const ImportantCtaBox = memo(ImportantCtaBoxComponent) as typeof ImportantCtaBoxComponent;
